// TypeScript-Mirror der **Cloud-API**-Schemas (G1/G2, harter Cut-over).
// Per Hand gepflegt — Schema-Aenderungen geben hier einen Compile-Fehler.
//
// Unterschiede zum frueheren lokalen Backend: UUID-IDs (string), schlankeres
// Beleg-Shape (kein OCR/Match/Audit — das lebt auf dem Bot-Host), Pagination
// als {items,total,limit,offset}, PDF als Presigned-URL.

export type BelegStatus =
  | "eingegangen"
  | "review"
  | "bestaetigt"
  | "bmd_vorbereitet"
  | "bmd_uebernommen"
  | "fehler"
  | "verworfen";

// Provenienz der Mandant-Zuordnung (ADR-018, Cloud).
export type MandantSource =
  | "client_key"
  | "email_alias"
  | "sachbearbeiter"
  | "ocr"
  | "unassigned";

export interface MandantSummary {
  id: string;
  bmd_kundennr: string;
  name: string;
}

export interface Mandant {
  id: string;
  bmd_kundennr: string;
  name: string;
  vorname: string | null;
  contact_email: string | null;
  steuernr_fa: string | null;
  plz: string | null;
  ort: string | null;
  strasse: string | null;
  sachbearb_nr: string | null;
  is_wp: boolean | null;
  name_varianten: string[];
  email_domains: string[];
  aktiv: boolean;
  created_at: string;
  updated_at: string;
}

export interface BelegListItem {
  id: string;
  original_filename: string;
  status: BelegStatus;
  mandant_id: string | null;
  mandant: MandantSummary | null;
  mandant_source: MandantSource;
  mandant_confidence: number | null;
  file_size_bytes: number;
  mime_type: string;
  belegdatum: string | null;
  bemerkung: string | null;
  created_at: string;
  updated_at: string;
}

export interface BelegDetail extends BelegListItem {
  content_hash: string;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export type BelegListResponse = Paginated<BelegListItem>;
export type MandantListResponse = Paginated<Mandant>;

export interface BelegPdfResponse {
  url: string;
  expires_at: string;
}

export interface HealthOut {
  service: string;
  status: string;
  timestamp: string;
}

export interface UploadResult {
  id: string;
  status: BelegStatus;
  mandant_id: string | null;
  mandant_source: MandantSource;
  content_hash: string;
  original_filename: string;
  file_size_bytes: number;
  storage_key: string | null;
  created_at: string;
  duplicate: boolean;
}

// --- Klient-Einladungen (G3) ---

// Antwort beim Erzeugen — der `token` (Klartext) ist NUR hier sichtbar.
// `mail_sent` sagt, ob die Cloud eine Einladungs-Mail in Auftrag gegeben hat
// (H6/ADR-026) — false, wenn keine Ziel-Adresse vorlag.
export interface InvitationResponse {
  id: string;
  token: string;
  expires_at: string;
  mail_sent: boolean;
}

// --- Bulk-Einladung per CSV (H5, ADR-027) ---

export interface BulkInviteRowError {
  row_number: number;
  bmd_kundennr: string;
  email: string;
  reason: string;
}

export interface BulkInviteResult {
  created: number; // erzeugte + (im Hintergrund) zugestellte Einladungen
  total_rows: number; // gültige Datenzeilen der CSV
  not_found: string[]; // unbekannte Kundennummern
  errors: BulkInviteRowError[]; // übersprungene Zeilen mit Grund
}

// Listen-Eintrag — ohne Token, mit abgeleitetem Status.
export type InvitationStatus = "pending" | "redeemed" | "expired";

export interface InvitationListItem {
  id: string;
  status: InvitationStatus;
  created_at: string;
  expires_at: string;
  redeemed_at: string | null;
}

// --- Auth ---

export interface CurrentUser {
  id: string;
  email: string;
  full_name: string;
  tenant_id: string;
}

export interface LoginBody {
  email: string;
  password: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

// --- Request-Bodies ---

export interface MandantCreateBody {
  bmd_kundennr: string;
  name: string;
  vorname?: string | null;
  contact_email?: string | null;
  steuernr_fa?: string | null;
  plz?: string | null;
  ort?: string | null;
  strasse?: string | null;
  sachbearb_nr?: string | null;
  is_wp?: boolean | null;
  name_varianten?: string[];
  email_domains?: string[];
}

export interface MandantUpdateBody {
  bmd_kundennr?: string;
  name?: string;
  vorname?: string | null;
  contact_email?: string | null;
  steuernr_fa?: string | null;
  plz?: string | null;
  ort?: string | null;
  strasse?: string | null;
  sachbearb_nr?: string | null;
  is_wp?: boolean | null;
  name_varianten?: string[];
  email_domains?: string[];
  aktiv?: boolean;
}

export interface AssignMandantBody {
  mandant_id: string | null;
}

// --- UI-Helfer ---

export const STATUS_ORDER: BelegStatus[] = [
  "eingegangen",
  "review",
  "bestaetigt",
  "bmd_vorbereitet",
  "bmd_uebernommen",
  "fehler",
  "verworfen",
];

export const STATUS_LABEL: Record<BelegStatus, string> = {
  eingegangen: "Eingegangen",
  review: "Prüfung nötig",
  bestaetigt: "Bestätigt",
  bmd_vorbereitet: "BMD vorbereitet",
  bmd_uebernommen: "BMD übernommen",
  fehler: "Fehler",
  verworfen: "Verworfen",
};

export const MANDANT_SOURCE_LABEL: Record<MandantSource, string> = {
  client_key: "Klient-Zugang",
  email_alias: "E-Mail-Alias",
  sachbearbeiter: "Sachbearbeiter",
  ocr: "OCR (Vorschlag)",
  unassigned: "nicht zugeordnet",
};

// --- Admin-Cockpit (read-only, ADR-030 Phase 0) ---

export interface AdminOverview {
  mandanten: number;
  datasets: number;
  active_workflows: number;
  review_pending: number;
}

export interface DatasetColumn {
  key: string;
  label: string;
  type: string;
  is_key: boolean;
}

export interface DatasetSummary {
  id: string;
  name: string;
  kind: string;
  key_column: string;
  columns: DatasetColumn[];
  row_count: number;
}

export interface Baustein {
  action: string;
  beschreibung: string;
  liest: string[];
  schreibt: string[];
  param: string | null;
  regel: string | null;
}

export interface ReviewItem {
  id: string;
  original_filename: string;
  status: BelegStatus;
  mandant_id: string | null;
  mandant_source: MandantSource;
  mandant_confidence: number | null;
  created_at: string;
}
